import { Flex, Loader, PieChart, Table, Text } from '@components';
import RestrictedProductTab from '@components/RestrictedProductTab/RestrictedProductTab';
import {
  useUserPortfolioQuery,
  useUserTransactionsQuery,
} from '@features/api/userPortfolioSlice';
import { useUserQuery } from '@features/api/userSlice';
import { TableCell, TableRow } from '@mui/material';
import { FormattedMessage, useIntl } from 'react-intl';
import Background from '@assets/backgrounds/portfolio/desktop/portfolio-overview-overall-mask.png';
import BackgroundMobile from '@assets/backgrounds/portfolio/mobile/portfolio-overview-overall-mask.png';
import Card from '@components/Card/Card';
import { getTranslation } from '@utils/intl/intlUtils';
import {
  ChartsContainer,
  Container,
  ExposureAndBreakdownContainer,
  PieChartContainer,
} from './Overview.styles';
import { useNavigate } from 'react-router-dom';
import { useProductsQuery } from '@features/api/productsSlice';
import { Lock } from '@assets/svg';
import { TransactionStatus } from '@features/api/response/UserTransactions';
import { useMediaQueries } from '@utils/mediaQueries/mediaQueries';
import { InvestorQualificationStage } from '@features/api/response/User';
import { useLazyGetInvestmentIntentQuery } from '@features/api/investmentSlice';
import { Fragment, useContext, useEffect, useMemo } from 'react';
import InvestmentData from './InvestmentData';
import { COMBINED_CURRENCY } from './CurrencySelector';
import { CurrencyContext } from '@pages/WrapperPages/PortfolioBase/PortfolioBase';

const Overview: React.FC = () => {
  const intl = useIntl();
  const navigate = useNavigate();
  const { sm: isMobile } = useMediaQueries();

  const {
    currency: selectedCurrency,
    combinedCurrency: selectedCombinedCurrency,
  } = useContext(CurrencyContext);

  const { data: user, isLoading: isLoadingUser } = useUserQuery();
  const isProfileNotCompleted =
    user?.investorQualificationStage ===
    InvestorQualificationStage.AwaitingQuestionnaire;
  const isUnderReview =
    user?.investorQualificationStage ===
    InvestorQualificationStage.QuestionnaireSubmitted;

  const { data: transactions } = useUserTransactionsQuery(undefined, {
    refetchOnMountOrArgChange: true,
  });

  const [hasInvestedTransaction] = useMemo(() => {
    const investedStatusTransactions =
      transactions?.filter(
        (x) => x.status === TransactionStatus.Invested.toUpperCase()
      ) ?? [];
    return [investedStatusTransactions.length > 0];
  }, [transactions]);

  const { data, isLoading: isLoadingUserPortfolio } = useUserPortfolioQuery(
    undefined,
    { skip: !hasInvestedTransaction }
  );

  const { data: products, isLoading: isLoadingProducts } = useProductsQuery(
    undefined,
    {
      skip: !user,
    }
  );
  const activeProductId = products?.filter((x) => x.active)?.[0].id;

  const [getInvestmentIntentQuery, { data: investmentIntent }] =
    useLazyGetInvestmentIntentQuery();

  useEffect(() => {
    if (
      activeProductId &&
      user?.investorQualificationStage ===
        InvestorQualificationStage.CompletedQualified &&
      !transactions?.length
    ) {
      getInvestmentIntentQuery({
        productId: activeProductId,
      });
    }
  }, [user, transactions, getInvestmentIntentQuery, activeProductId]);

  if (isLoadingUser || isLoadingUserPortfolio || isLoadingProducts)
    return (
      <Loader
        isLoading={isLoadingUser || isLoadingUserPortfolio || isLoadingProducts}
      />
    );

  const profileIncomplete = isProfileNotCompleted || isUnderReview;
  const showInvestButton = products && !transactions?.length;
  const transactionInReview = transactions?.find(
    (x) =>
      x.status === TransactionStatus.InReview ||
      x.status === TransactionStatus.PendingCustomerSignature
  );
  const awaitingTransfer = transactions?.find(
    (x) =>
      x.status === TransactionStatus.AwaitingPayment ||
      x.status === TransactionStatus.AwaitingFirstClose ||
      x.status === TransactionStatus.Approved
  );

  const shouldHideRestrictedContent =
    transactions?.length &&
    transactions?.find((x) => x.status === TransactionStatus.Invested);

  const handleInvestButtonClick = (): void => {
    if (
      user?.personalDetailsLocked ||
      investmentIntent?.id ||
      transactions?.length
    ) {
      navigate(`/investment-subscription/${activeProductId}/amount`, {
        state: {
          progress: 0,
        },
      });
    } else {
      navigate(`/investment-get-started/${activeProductId}/subscription-intro`);
    }
  };

  if (!shouldHideRestrictedContent) {
    let textTranslationId = '';
    let button;

    if (profileIncomplete) {
      textTranslationId = !isUnderReview
        ? 'portfolio.completeQuestionnaireAndInvestToUnlock'
        : 'home.profileUnderReview';
      button = !isUnderReview
        ? {
            label: intl.formatMessage({ id: 'misc.completeQuestionnaire' }),
            onClick: () => navigate(`/questionnaire-intro`),
            iconLeft: <Lock />,
          }
        : undefined;
    } else if (awaitingTransfer) {
      textTranslationId = 'portfolio.awaitingTransfer';
    } else if (transactionInReview) {
      textTranslationId = 'portfolio.investmentUnderReview';
    } else if (showInvestButton) {
      textTranslationId = 'portfolio.investToUnlock';
      button = {
        label: intl.formatMessage({ id: 'product.invest' }),
        onClick: handleInvestButtonClick,
      };
    } else {
      textTranslationId = 'portfolio.investToUnlock';
    }

    return (
      <Flex flex='1 0 100%'>
        <RestrictedProductTab
          backgroundImageUrl={!isMobile ? Background : BackgroundMobile}
          text={intl.formatMessage(
            { id: textTranslationId },
            {
              br: <br />,
            }
          )}
          button={button}
        />
      </Flex>
    );
  }

  return (
    <Container data-testid='overview-container'>
      <InvestmentData
        data={data?.investmentData.find(
          (x) =>
            x.currencyCode === selectedCurrency ||
            (selectedCurrency === COMBINED_CURRENCY &&
              x.currencyCode ===
                `${COMBINED_CURRENCY}-${selectedCombinedCurrency}`)
        )}
        currency={selectedCombinedCurrency}
      />
      <ChartsContainer>
        <PieChartContainer>
          <PieChart
            data={
              data?.geographicalExposureChart.chartData?.map((x) => ({
                id: x.name,
                value: Number(x.percent),
              })) ?? []
            }
            valueFormat={(v) => `${v}%`}
            title={getTranslation(intl, 'portfolio.geographicalExposure')}
            cardTooltipText={getTranslation(
              intl,
              'portfolio.geographicalExposure'
            )}
          />
        </PieChartContainer>
        <PieChartContainer>
          <PieChart
            data={
              data?.instrumentTypeChart.chartData?.map((x) => ({
                id: x.name,
                value: Number(x.percent),
              })) ?? []
            }
            valueFormat={(v) => `${v}%`}
            title={getTranslation(intl, 'portfolio.instrumentType')}
            cardTooltipText={getTranslation(intl, 'portfolio.instrumentType')}
          />
        </PieChartContainer>
      </ChartsContainer>
      <ChartsContainer>
        <PieChartContainer>
          <PieChart
            data={
              data?.investmentStrategyChart.chartData?.map((x) => ({
                id: x.name,
                value: Number(x.percent),
              })) ?? []
            }
            valueFormat={(v) => `${v}%`}
            title={getTranslation(intl, 'portfolio.investmentStrategy')}
            cardTooltipText={getTranslation(
              intl,
              'portfolio.investmentStrategy'
            )}
          />
        </PieChartContainer>
        <PieChartContainer>
          <PieChart
            data={
              data?.industryAllocationChart.chartData?.map((x) => ({
                id: x.name,
                value: Number(x.percent),
              })) ?? []
            }
            valueFormat={(v) => `${v}%`}
            title={getTranslation(intl, 'portfolio.industryAllocation')}
            cardTooltipText={getTranslation(
              intl,
              'portfolio.industryAllocation'
            )}
          />
        </PieChartContainer>
      </ChartsContainer>
      <ExposureAndBreakdownContainer>
        <Flex
          sx={{
            flex: 1,
            width: {
              xs: '100%',
              md: 'auto',
            },
          }}
        >
          <Card
            title={getTranslation(intl, 'portfolio.topInvestments')}
            tooltipText={getTranslation(intl, 'portfolio.topInvestments')}
          >
            <Table
              header={
                <TableRow>
                  <TableCell align='left'>
                    <Text variant='bodyBig' fontWeight='fontWeightSemiBold'>
                      <FormattedMessage id='portfolio.topInvestmentsColumns.generalPartner' />
                    </Text>
                  </TableCell>
                  <TableCell align='left'>
                    <Text variant='bodyBig' fontWeight='fontWeightSemiBold'>
                      <FormattedMessage id='portfolio.topInvestmentsColumns.region' />
                    </Text>
                  </TableCell>
                  <TableCell align='left'>
                    <Text variant='bodyBig' fontWeight='fontWeightSemiBold'>
                      <FormattedMessage id='portfolio.topInvestmentsColumns.instrumentType' />
                    </Text>
                  </TableCell>
                  <TableCell align='left'>
                    <Text variant='bodyBig' fontWeight='fontWeightSemiBold'>
                      <FormattedMessage id='portfolio.topInvestmentsColumns.commentary' />
                    </Text>
                  </TableCell>
                </TableRow>
              }
              body={data?.topInvestments.map((x) => (
                <TableRow>
                  <TableCell>{x.generalPartner}</TableCell>
                  <TableCell>{x.region}</TableCell>
                  <TableCell>{x.instrumentType}</TableCell>
                  <TableCell>{x.commentary}</TableCell>
                </TableRow>
              ))}
            />
          </Card>
        </Flex>
      </ExposureAndBreakdownContainer>
      <Flex
        sx={{
          flex: 1,
          width: {
            xs: '100%',
            md: 'auto',
          },
      }}
      >
        <Card
          title={getTranslation(intl, 'portfolio.commentary')}
          tooltipText={getTranslation(intl, 'portfolio.commentary')}
        >
          <Text variant='bodyBig' fontWeight='fontWeightMedium'>
            {data?.commentary.split('\\n').map((item, index, arr) => {
              return (
                <Fragment key={index}>
                  {item}
                  <br />
                  {index !== arr.length - 1 ? <br /> : null}
                </Fragment>
              );
            })}
          </Text>
        </Card>
      </Flex>
    </Container>
  );
};

export default Overview;
